/** The lab's calls: complete lemmas (/api/lab) and the reverse index (/api/lex/index). */
import Client from '@/hexapla-core/client'
import { Lemma } from '@/hexapla-core/domains/lemmas/schema'
import { hydrateLemma } from '@/hexapla-core/domains/lemmas/hydrate'
import {
  FieldName,
  fieldKey,
  fieldName,
  FieldRef,
  IndexLexeme,
  Language,
  PrintedField,
} from '@/components/lemmas/renderer/wordLayer'
import { useApiUrl } from '@/compositions/servicebuilder/useApiUrl'

const api = useApiUrl()
const q = encodeURIComponent

export interface LabBook {
  id: number
  name: string
  chapters: { chapter: number; lemmas: number }[]
}

/** A printed text of a lemma, named, with its words placed in it. */
export interface NamedField extends PrintedField {
  field: FieldName
}

/** A lemma's printed texts, each with its words placed in it. */
export interface LemmaFields {
  fields: NamedField[]
}

/** A lemma's fields by key, for the word layer. */
export type FieldMap = Map<string, PrintedField>

export const fieldMap = (f: LemmaFields): FieldMap =>
  new Map((f.fields ?? []).map((x) => [fieldKey(x.field), x]))

/** One printed text of a lemma's fields. */
export const fieldOf = (fields: FieldMap, ref: FieldRef): PrintedField | null =>
  fields.get(fieldKey(fieldName(ref))) ?? null

/**
 * Where a column's word comes from: a printed reading (`head`), or beyond it
 * (`Beyond`), with the witnesses that read or credit it.
 */
export type Beyond = 'var' | 'attr' | 'wit'
export type Origin = 'head' | Beyond

/**
 * Every origin, named once: its name on the search's switch (`short`; the
 * head is the lemma line), what search calls it (`label`, `src:` + key), what
 * it covers; beyond the printed readings, its badge, its count line and its
 * explanation.
 */
export const ORIGINS: Record<
  Origin,
  { short: string; label: string; covers: string; badge: string; counted: string; title: string }
> = {
  head: {
    short: 'Lemma',
    label: 'printed readings',
    covers: 'the readings the Edition prints',
    badge: '',
    counted: '',
    title: '',
  },
  var: {
    short: 'Var',
    label: 'Var readings',
    covers: 'the Var line’s readings',
    badge: 'Var',
    counted: 'in Var readings',
    title: 'A Var reading: a variant of the reading, as these witnesses read it',
  },
  attr: {
    short: 'Attr',
    label: 'Attr lines',
    covers: 'columns an Attr line credits, and its readings',
    badge: 'Attr',
    counted: 'on Attr lines',
    title: 'An Attr line: these witnesses credit the reading to this column, or read it so',
  },
  wit: {
    short: 'Wit',
    label: 'Wit lines',
    covers: 'words a Wit line cites witnesses for, and its readings',
    badge: 'Wit',
    counted: 'on Wit lines',
    title: 'A Wit line: these witnesses are cited for the word, or read it so',
  },
}

/** Every origin, in order; and those beyond the printed readings. */
export const ORIGIN_KEYS = Object.keys(ORIGINS) as Origin[]
export const BEYOND_KEYS = ORIGIN_KEYS.filter((o): o is Beyond => o !== 'head')

export interface Sourced {
  /** The line it is printed on. */
  origin: Origin
  origin_id: number | null
  witnesses: string[]
  /** Its Var reading is marked "= 𝔐", agreeing with the Hebrew. */
  agrees_with_mt: boolean
}

export interface Rendering {
  lexeme: IndexLexeme
  count: number
  unconfirmed: number
}

/** One column of the Hexapla (LXX, α′, σ′, θ′…). */
export interface ColumnSummary {
  column: string
  total: number
  renderings: Rendering[]
  unaligned: number
  /** Beside `total` (the printed readings): its words beyond them, by origin. */
  beyond: Partial<Record<Beyond, number>>
}

export interface IndexSummary {
  lexeme: IndexLexeme
  total: number
  columns: ColumnSummary[]
}

export interface RefRow extends Sourced {
  column: string
  book: string
  chapter: number
  verse: number
  surface: string
  lemma_id: number | null
  confident: boolean
}

export interface RefPage {
  total: number
  page: number
  per_page: number
  refs: RefRow[]
}

export const books = () => Client.get<LabBook[]>(`${api}/api/lab/books`)

export const chapter = async (bookId: number, ch: number) =>
  (await Client.get<Lemma[]>(`${api}/api/lab/books/${bookId}/chapters/${ch}`)).map(hydrateLemma)

export const lemma = async (id: number) => hydrateLemma(await Client.get<Lemma>(`${api}/api/lab/lemmas/${id}`))

export const lemmaFields = (id: number) => Client.get<LemmaFields>(`${api}/api/lex/index/lemma/${id}`)

// Summaries are cached for the session: a hover prefetches, the click reuses it.
const summaries = new Map<string, Promise<IndexSummary>>()

export const summary = (language: Language, key: string): Promise<IndexSummary> => {
  const k = `${language}:${key}`
  if (!summaries.has(k)) {
    const p = Client.get<IndexSummary>(`${api}/api/lex/index/${language}/${q(key)}`)
    p.catch(() => summaries.delete(k))
    summaries.set(k, p)
  }
  return summaries.get(k) as Promise<IndexSummary>
}

export const refs = (
  language: Language,
  key: string,
  column: string,
  counterpart: string | null,
  page = 0,
  origin: Origin | null = null
) =>
  Client.get<RefPage>(
    `${api}/api/lex/index/refs/${language}/${q(key)}?column=${q(column)}` +
      (counterpart ? `&counterpart=${q(counterpart)}` : '') +
      (origin ? `&src=${origin}` : '') +
      `&page=${page}`
  )

// ---------- search ----------

export interface ParsingText {
  text: string
}

/** One word a search found. */
export interface Hit extends Sourced {
  column: string
  book: string
  chapter: number
  verse: number
  surface: string
  word_id: string
  lexeme: IndexLexeme | null
  renders: IndexLexeme[]
  lemma_id: number | null
  attestation_id: number | null
  confident: boolean
  parsing: ParsingText | null
  renders_parsing: ParsingText[]
}

export interface BookCount {
  book: string
  count: number
}

export interface ColumnCount {
  column: string
  count: number
}

export interface SearchResult {
  /** The query as understood: its canonical text, and the deep link. */
  query: string
  total: number
  page: number
  per_page: number
  hits: Hit[]
  by_book: BookCount[]
  by_column: ColumnCount[]
  by_origin: { origin: Origin; count: number }[]
}

/** A search, or the reason the query was refused (it names the term). */
export const search = (q: string, page = 0) =>
  Client.get<SearchResult>(`${api}/api/lex/index/search?q=${encodeURIComponent(q)}&page=${page}`)

// ---------- lexicon cards ----------

/** A run of a card's text: the only markup a card carries. */
export type Run =
  | { kind: 'text' | 'definition' | 'emphasis' | 'hebrew' | 'greek' | 'foreign'; text: string }
  | { kind: 'reference'; text: string; verse?: { book: string; chapter: number; verse: number } }
  | { kind: 'cross_reference'; text: string; entry: string }

export interface Paragraph {
  label?: string
  depth: number
  runs: Run[]
}

export interface LexiconCard {
  lexicon: string
  /** The language of the words it describes. */
  language: Language
  title: string
  attribution: string
  entry: string
  headword: string
  translit: string | null
  pos: string | null
  gloss: string | null
  body: Paragraph[]
}

export interface LexiconEntry {
  lexeme: IndexLexeme
  cards: LexiconCard[]
}

// ---------- relations ----------

export interface Pair {
  a: string
  b: string
  shared: number
  joint: number
  same: number
  different: number
  against_lxx: number
  independent_agreement: number | null
  co_attested: number
}

export interface Reach {
  column: string
  renders: number
  lxx_silent: number
  lemmas: number
}

export interface Agreement {
  columns: Reach[]
  pairs: Pair[]
}

export type Relation = 'joint' | 'same' | 'different' | 'against_lxx' | 'lxx_silent'

export interface SharedMorpheme {
  book: string
  chapter: number
  verse: number
  mt_link: string
  hebrew: string
  parsing: ParsingText
  renderings: Hit[]
}

export interface Evidence {
  total: number
  page: number
  per_page: number
  items: SharedMorpheme[]
}

export interface LexemeConsistency {
  hebrew: IndexLexeme
  total: number
  distinct: number
  dominant: IndexLexeme
  dominant_share: number
  entropy: number
}

export interface ColumnConsistency {
  column: string
  lexemes_counted: number
  renderings: number
  dominant_share: number | null
  entropy: number | null
  lexemes: LexemeConsistency[]
}

export interface RootRendering {
  root: string
  hebrew: IndexLexeme[]
  total: number
  dominant: string
  family_share: number
  renderings: { hebrew: IndexLexeme; greek: IndexLexeme; count: number }[]
}

export interface ColumnFamilies {
  column: string
  roots_counted: number
  renderings: number
  family_share: number | null
  roots: RootRendering[]
}

export interface Divergence {
  mt_words: number
  mt_unrendered: number
  lxx_words: number
  lxx_pluses: number
  minus_share: number | null
  plus_share: number | null
}

export interface VerseWord {
  word_id: string
  surface: string
}

export interface VerseSignals {
  book: string
  chapter: number
  verse: number
  mt_words: number
  lxx_words: number
  mt_unrendered: VerseWord[]
  lxx_pluses: VerseWord[]
}

export interface Vorlage {
  total: Divergence
  places: (Divergence & { book: string | null; chapter: number | null })[]
  verses: VerseSignals[]
}

/** What to count over: some books (`Exod`, `Gen-Deut`), one Hebrew lexeme. */
export interface Scope {
  books?: string
  hebrew?: string
}

const params = (p: Record<string, string | number | undefined | null>) =>
  Object.entries(p)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&')

export const evidence = (a: string, b: string, relation: Relation, scope: Scope, page = 0) =>
  Client.get<Evidence>(`${api}/api/lex/index/relations/evidence?${params({ a, b, relation, ...scope, page })}`)

export const families = (scope: Scope, min = 3, top = 20) =>
  Client.get<ColumnFamilies[]>(`${api}/api/lex/index/relations/families?${params({ ...scope, min, top })}`)

// ---------- layers ----------

export interface Overview {
  columns: { column: string; words: number; readings: number; places: { book: string | null; chapter: number | null; count: number }[] }[]
  agreement: Agreement
  consistency: ColumnConsistency[]
  vorlage: Vorlage
}

export interface LexemeLayer {
  summary: IndexSummary
  cards: LexiconCard[]
  consistency: ColumnConsistency[]
  agreement: Agreement | null
}

export interface MorphemeRelations {
  morpheme: SharedMorpheme
  relations: { a: string; b: string; relation: Relation }[]
}

export interface LemmaLayer {
  fields: LemmaFields
  verse: VerseSignals | null
  lexemes: LexiconEntry[]
  morphemes: MorphemeRelations[]
}

export const overview = (scope: Scope, top = 10) =>
  Client.get<Overview>(`${api}/api/lex/index/layers/overview?${params({ ...scope, top })}`)

const lexemeLayers = new Map<string, Promise<LexemeLayer>>()

/** A lexeme's layer (cards, translators), cached for the session like summaries. */
export const lexemeLayer = (language: Language, key: string): Promise<LexemeLayer> => {
  const k = `${language}:${key}`
  if (!lexemeLayers.has(k)) {
    const p = Client.get<LexemeLayer>(`${api}/api/lex/index/layers/lexeme/${language}/${q(key)}`)
    p.catch(() => lexemeLayers.delete(k))
    lexemeLayers.set(k, p)
  }
  return lexemeLayers.get(k) as Promise<LexemeLayer>
}

export const lemmaLayer = (id: number) => Client.get<LemmaLayer>(`${api}/api/lex/index/layers/lemma/${id}`)

// ---------- credits ----------

/** One source the served index is built from. */
export interface IndexSource {
  file: string
  credit: string
  licence: string
  url: string
}

export const sources = () => Client.get<{ sources: IndexSource[] }>(`${api}/api/lex/index/sources`)

/** Sources grouped by whom to credit, each with its licences, in order. */
export interface Credit {
  credit: string
  licences: string[]
  sources: IndexSource[]
}

export const credits = (all: IndexSource[]): Credit[] => {
  const by = new Map<string, Credit>()
  for (const s of all) {
    const c = by.get(s.credit) ?? { credit: s.credit, licences: [], sources: [] }
    if (!c.licences.includes(s.licence)) c.licences.push(s.licence)
    c.sources.push(s)
    by.set(s.credit, c)
  }
  return Array.from(by.values())
}
